// Condition order and the Fitts design, both derived deterministically from
// the participant id. Deterministic matters: a participant who reloads the
// page mid-session must get the same order and the same trial sequence back,
// and the analysis must be able to reconstruct a session from its id alone.

// FNV-1a over the id, used to seed everything below.
export function hashId(str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) }
  return h >>> 0
}

// mulberry32: small, seedable, good enough for shuffling a nine-cell design.
export function seededRandom(seed) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// A balanced Latin square gives each condition each position equally often and
// balances immediate order (each condition preceded by each other equally),
// which a plain shuffle does not. For n conditions this is the standard
// construction; for odd n the square is reversed and appended to balance.
export function balancedLatinSquare(items) {
  const n = items.length
  const rows = []
  for (let r = 0; r < n; r++) {
    const row = []
    for (let c = 0; c < n; c++) {
      // 0, 1, n-1, 2, n-2, ... sequence, rotated by row
      const idx = c % 2 === 0 ? c / 2 : n - ((c + 1) / 2)
      row.push(items[(idx + r) % n])
    }
    rows.push(row)
  }
  if (n % 2 === 1) rows.push(...rows.map((row) => [...row].reverse()))
  return rows
}

// Which row of the square this participant gets.
export function conditionOrder(participantId, interfaces) {
  const square = balancedLatinSquare(interfaces)
  return square[hashId(participantId) % square.length]
}

// The Fitts manipulation, within the pads condition.
//
// Widths and distances are chosen together so the nine cells span an index of
// difficulty of roughly 1.4 to 4.2 bits. Too narrow a span and the regression
// line looks flat whatever the data says; a cell at ID 1.0 (D == W) is free
// and wastes one of only nine points.
//
// The largest distance has to stay reachable on a phone held in one hand, so
// 460 px is the ceiling -- that is a vertical move on a 390x844 screen, with
// the home pad at the bottom. The smallest width is deliberately below the
// 44 px touch-target guideline: difficulty is the independent variable here.
//
// ID = log2(D/W + 1)  (Shannon formulation, ISO 9241-411).
export const FITTS_WIDTHS = [88, 48, 26]
export const FITTS_DISTANCES = [140, 300, 460]

export function indexOfDifficulty(distance, width) {
  return Math.log2(distance / width + 1)
}

// 3 widths x 3 distances x `reps`, shuffled deterministically, with warm-up
// trials prepended. Two reps costs about twenty seconds and is what makes a
// per-participant slope plottable rather than only a class-pooled one.
export function fittsDesign(participantId, { reps = 2, warmup = 2 } = {}) {
  const rnd = seededRandom(hashId(participantId) ^ 0x5eed)
  const cells = []
  for (let r = 0; r < reps; r++) {
    for (const width of FITTS_WIDTHS) {
      for (const distance of FITTS_DISTANCES) {
        cells.push({ width, distance, id: indexOfDifficulty(distance, width) })
      }
    }
  }
  // Fisher-Yates with the seeded generator
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[cells[i], cells[j]] = [cells[j], cells[i]]
  }
  const warmupTrials = Array.from({ length: warmup }, () => ({
    width: FITTS_WIDTHS[0], distance: FITTS_DISTANCES[0], id: indexOfDifficulty(FITTS_DISTANCES[0], FITTS_WIDTHS[0]), warmup: true,
  }))
  return [...warmupTrials, ...cells.map((c) => ({ ...c, warmup: false }))]
}

// Cue sequence for a non-pads interface: every sound equally often, never the
// same sound twice in a row (a repeat lets the participant pre-plan, which
// contaminates reaction time).
export function cueSequence(participantId, sounds, n) {
  const rnd = seededRandom(hashId(participantId) ^ 0xc0ffee)
  const out = []
  let last = null
  while (out.length < n) {
    const pool = sounds.filter((s) => s !== last)
    const pick = pool[Math.floor(rnd() * pool.length)]
    out.push(pick)
    last = pick
  }
  return out
}
